<template>
  <div style="height:100%">
    <portal to="app-header">
      <span>{{ $t('monthlyPlan.title') }}</span>
      <v-btn icon small class="ml-4 mb-1">
        <v-icon v-text="'$info'"></v-icon>
      </v-btn>
      <v-btn
        icon
        small
        class="ml-2 mb-1"
        @click="refreshData"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </portal>

    <v-container fluid>
      <!-- Toolbar with buttons -->
      <div class="d-flex align-center justify-end mb-3">
        <v-menu
          v-model="monthPickerMenu"
          :close-on-content-click="false"
          offset-y
          min-width="290px"
        >
          <template #activator="{ on, attrs }">
            <v-btn
              small
              outlined
              color="primary"
              class="text-none mr-2"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon left small>mdi-calendar</v-icon>
              {{ displayMonth }}
            </v-btn>
          </template>
          <v-date-picker
            :value="pickerMonth"
            type="month"
            @input="onMonthChange"
          />
        </v-menu>
        <v-btn
          small
          outlined
          color="primary"
          class="text-none mr-2"
          @click="downloadTemplate"
        >
          <v-icon left small>mdi-download</v-icon>
          Download CSV template
        </v-btn>
        <upload-csv-btn @uploaded="onPlanSaved" />
        <monthly-plan-form-btn @saved="onPlanSaved" />
        <v-btn
          small
          outlined
          color="primary"
          class="text-none mr-2"
          @click="downloadTableData"
        >
          <v-icon left small>mdi-file-download</v-icon>
          Download
        </v-btn>
        <v-btn
          small
          outlined
          color="primary"
          class="text-none"
          @click="toggleDrawer"
        >
          <v-icon left small>mdi-filter-variant</v-icon>
          Filters
        </v-btn>
      </div>
      <v-card outlined>
        <v-card-text v-if="loading" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" />
          <div class="mt-2 caption">
            {{ $t('monthlyPlan.loading') }}
          </div>
        </v-card-text>
        <v-data-table
          v-else-if="filteredPlans.length"
          :headers="tableHeaders"
          :items="groupedPlans"
          :items-per-page="itemsPerPage"
          :footer-props="{
            'items-per-page-options': [10, 20, 50],
          }"
          dense
          class="elevation-0"
        >
          <template #item.quantity="{ item }">
            {{ item.quantityDisplay.toLocaleString() }}
          </template>
          <template #item.productionintonnage="{ item }">
            {{ item.productionintonnage || '-' }}
          </template>
          <template #item.partname="{ item }">
            <a
              href="#"
              class="primary--text"
              @click.prevent="showPartDetail(item)"
            >
              {{ item.partname }}
            </a>
          </template>
          <template #item.actualtonnage="{ item }">
            {{ item.actualDisplay
              ? item.actualDisplay.toLocaleString()
              : (weightUnit === 'grams' ? '0.000' : '0') }}
          </template>
          <template #item.monthyear="{ item }">
            {{ formatMonth(item.monthyear) }}
          </template>
          <template #item.machinename="{ item }">
            {{ item.machinename || '-' }}
          </template>
          <template #item.actions="{ item }">
            <v-btn icon x-small class="mr-1" @click="editPlan(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon x-small color="error" @click="deletePlan(item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
          <template #body.append>
            <tr
              class="font-weight-bold"
              :class="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"
            >
              <td>Total</td>
              <td></td>
              <td>{{ totalQuantityDisplay }}</td>
              <td>{{ totalTonnage }}</td>
              <td>{{ totalActualTonnage }}</td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          </template>
        </v-data-table>
        <v-card-text v-else class="text-center pa-8">
          <v-icon size="48" color="grey lighten-1">
            mdi-calendar-blank
          </v-icon>
          <div class="mt-2 body-2 grey--text">
            {{ $t('monthlyPlan.noRecords') }}
          </div>
        </v-card-text>
      </v-card>

    </v-container>

    <!-- Part Detail Dialog -->
    <v-dialog v-model="partDetailDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold">
          {{ partDetailName }} — Production Detail
          <v-spacer />
          <v-btn
            icon
            small
            class="mr-1"
            @click="downloadPartDetail"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
          <v-btn icon @click="partDetailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 500px;">
          <v-simple-table dense v-if="partDetailRows.length">
            <thead>
              <tr>
                <th>Date</th>
                <th>Machine</th>
                <th>Plan ID</th>
                <th class="text-right">Produced</th>
                <th class="text-right">Produced Wt (Kgs)</th>
                <th class="text-right">Accepted</th>
                <th class="text-right">Rejected</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in partDetailRows" :key="i">
                <td>{{ r.date }}</td>
                <td>{{ r.machinename }}</td>
                <td>{{ r.planid }}</td>
                <td class="text-right">{{ r.produced }}</td>
                <td class="text-right">
                  {{ producedWeightDisplay(r.producedweightinkgs) }}
                </td>
                <td class="text-right">{{ r.accepted }}</td>
                <td class="text-right">{{ r.rejected }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <div v-else class="text-center pa-4 grey--text">
            No production data found for this part.
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold">
          Confirm Delete
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          Are you sure you want to delete the plan for
          <strong>{{ deleteItemName }}</strong>?
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text class="text-none" @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="error"
            class="text-none"
            :loading="deleting"
            @click="confirmDelete"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Filter Drawer -->
    <filter-drawer />
  </div>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapActions,
  mapMutations,
} from 'vuex';
import MonthlyPlanFormBtn from '../components/MonthlyPlanFormBtn.vue';
import UploadCsvBtn from '../components/UploadCsvBtn.vue';
import FilterDrawer from '../components/FilterDrawer.vue';

export default {
  name: 'MonthlyPlanning',
  components: { MonthlyPlanFormBtn, UploadCsvBtn, FilterDrawer },
  data() {
    return {
      monthPickerMenu: false,
      partDetailDialog: false,
      partDetailRows: [],
      partDetailName: '',
      deleteDialog: false,
      deleteItem: null,
      deleteItemName: '',
      deleting: false,
      itemsPerPage: 10,
    };
  },
  computed: {
    tableHeaders() {
      return [
        { text: 'Part', value: 'partname', class: 'text-center' },
        { text: 'Color', value: 'color', class: 'text-center' },
        { text: 'Quantity', value: 'quantity', class: 'text-center' },
        {
          text: 'Planned Tonnage',
          value: 'productionintonnage',
          class: 'text-center',
        },
        {
          text: 'Actual Tonnage',
          value: 'actualtonnage',
          class: 'text-center',
        },
        { text: 'Month', value: 'monthyear', class: 'text-center' },
        { text: 'Machine', value: 'machinename', class: 'text-center' },
        {
          text: 'Actions',
          value: 'actions',
          class: 'text-center',
          sortable: false,
        },
      ];
    },
    ...mapState('monthlyPlanning', [
      'loading', 'selectedMonthYear', 'actualProductionData', 'parts',
      'weightUnit',
    ]),
    ...mapGetters('monthlyPlanning', [
      'filteredPlans', 'actualTonnageMap', 'actualKgMap',
    ]),
    partWeightMap() {
      // partname -> net weight (grams) from the part master.
      const m = {};
      (this.parts || []).forEach((p) => {
        const wt = p.netweight || p.weight || p.netwt;
        if (p.partname && wt != null && wt !== '') {
          m[p.partname] = parseFloat(wt) || 0;
        }
      });
      return m;
    },
    groupedPlans() {
      const map = {};
      this.filteredPlans.forEach((p) => {
        const key = `${p.partname}||${p.color || '-'}||${p.monthyear}`;
        if (!map[key]) {
          map[key] = {
            partname: p.partname,
            color: p.color || '-',
            monthyear: p.monthyear,
            quantity: 0,
            netweight: 0,
            productionintonnage: 0,
            machinename: p.machinename || '-',
            _ids: [],
          };
        }
        map[key].quantity += Number(p.quantity) || 0;
        map[key].productionintonnage += parseFloat(p.productionintonnage) || 0;
        // Net weight per piece (grams): prefer plan record, else part master.
        const recWt = parseFloat(p.netweight) || 0;
        if (recWt) {
          map[key].netweight = recWt;
        } else if (this.partWeightMap[p.partname]) {
          map[key].netweight = this.partWeightMap[p.partname];
        }
        map[key]._ids.push(p._id || p.id);
      });
      return Object.values(map).map((row) => {
        const actualKey = `${row.partname}||${row.color}`;
        const planned = parseFloat(row.productionintonnage.toFixed(3));
        // Quantity in grams = planned tonnage (as grams) / net weight (grams).
        // Planned tonnage is in tonnes, so convert to grams via x 1,000,000.
        const quantityInGrams = row.netweight
          ? Math.round((planned * 1000000) / row.netweight)
          : 0;
        // Kg quantity is the grams value / 1000.
        const quantityInKg = quantityInGrams
          ? Math.round(quantityInGrams / 1000)
          : row.quantity;
        // Actual: raw kg from report, and tonnes (kg/1000).
        const actualTonnes = this.actualTonnageMap[actualKey] || 0;
        const actualKg = this.actualKgMap[actualKey] || 0;
        // Display values based on the selected weight unit.
        const quantityDisplay = this.weightUnit === 'grams'
          ? quantityInGrams
          : quantityInKg;
        const actualDisplay = this.weightUnit === 'grams'
          ? actualTonnes
          : actualKg;
        return {
          ...row,
          quantityInGrams,
          quantityInKg,
          quantityDisplay,
          actualDisplay,
          productionintonnage: planned,
          actualtonnage: actualTonnes,
        };
      });
    },
    displayMonth() {
      const v = this.selectedMonthYear;
      if (!v) return 'Select Month';
      const s = String(v);
      const year = s.substring(0, 4);
      const month = s.substring(4, 6);
      const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
      ];
      return `${months[parseInt(month, 10) - 1]} ${year}`;
    },
    pickerMonth() {
      const v = this.selectedMonthYear;
      if (!v) return null;
      const s = String(v);
      return `${s.substring(0, 4)}-${s.substring(4, 6)}`;
    },
    totalTonnage() {
      const sum = this.groupedPlans.reduce(
        (acc, p) => acc + (parseFloat(p.productionintonnage) || 0),
        0,
      );
      return sum.toFixed(3);
    },
    grandTotalQty() {
      return this.groupedPlans.reduce(
        (acc, p) => acc + (Number(p.quantity) || 0),
        0,
      );
    },
    totalQuantityDisplay() {
      const sum = this.groupedPlans.reduce(
        (acc, p) => acc + (Number(p.quantityDisplay) || 0),
        0,
      );
      return sum.toLocaleString();
    },
    totalActualTonnage() {
      const sum = this.groupedPlans.reduce(
        (acc, p) => acc + (parseFloat(p.actualDisplay) || 0),
        0,
      );
      // Tonnes shown to 3 decimals; Kg to 2.
      return this.weightUnit === 'grams'
        ? sum.toFixed(3)
        : sum.toFixed(2);
    },
  },
  async created() {
    await this.initElement();
    await this.fetchParts();
    await this.fetchColors();
    await this.fetchMachines();
    await this.fetchMonthlyPlans();
    await this.fetchActualProduction();
  },
  methods: {
    ...mapActions('monthlyPlanning', [
      'initElement',
      'fetchMonthlyPlans',
      'fetchParts',
      'fetchColors',
      'fetchMachines',
      'fetchActualProduction',
    ]),
    ...mapMutations('monthlyPlanning', [
      'setDrawer',
      'setSelectedMonthYear',
      'setSelectedPartDetail',
    ]),
    toggleDrawer() {
      this.setDrawer(true);
    },
    onMonthChange(val) {
      this.monthPickerMenu = false;
      const monthYear = parseInt(`${val.replace('-', '')}01`, 10);
      this.setSelectedMonthYear(monthYear);
      this.fetchMonthlyPlans();
      this.fetchActualProduction();
    },
    onPlanSaved() {
      this.fetchMonthlyPlans();
    },
    refreshData() {
      this.fetchMonthlyPlans();
      this.fetchActualProduction();
    },
    editPlan(item) {
      // Open edit - reuse the form btn by emitting event
      this.$root.$emit('edit-monthly-plan', item);
    },
    producedWeightDisplay(value) {
      // Source produced weight is in kg. In Kg mode show grams-equivalent
      // (x1000), in Grams mode show the raw value, per requirement.
      const v = parseFloat(value) || 0;
      if (this.weightUnit === 'kgs') {
        return Math.round(v * 1000).toLocaleString();
      }
      return v;
    },
    showPartDetail(item) {
      const data = this.actualProductionData || [];
      this.partDetailRows = data.filter(
        (r) => r.partname === item.partname
          && (r.color === item.color || (!r.color && item.color === '-')),
      );
      this.partDetailName = `${item.partname} - ${item.color}`;
      this.partDetailDialog = true;
    },
    deletePlan(item) {
      this.deleteItem = item;
      this.deleteItemName = item.partname;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      if (!this.deleteItem) return;
      this.deleting = true;
      const item = this.deleteItem;
      const ids = item._ids || [];
      const singleId = item._id || item.id;
      if (singleId) {
        ids.push(singleId);
      }
      if (ids.length) {
        const promises = ids.map((id) => this.$store.dispatch(
          'element/deleteRecordById',
          { elementName: 'monthlyplan', id },
          { root: true },
        ));
        await Promise.all(promises);
      }
      this.deleting = false;
      this.deleteDialog = false;
      this.deleteItem = null;
      this.fetchMonthlyPlans();
    },
    downloadTemplate() {
      const csv = 'partname,color,quantity,monthyear,machinename\n'
        + 'BACIA 21ltr,White MB,5000,2026-08,BM-1-JMV-60\n'
        + 'Sfg-Termica Jumbo-75-Outer Body,Red MB,3000,2026-08,\n'
        + 'BACIA 125 LITROS,Blue MB,2000,2026-09,IM-1-CLF-1200\n';
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'monthly-plan-template.csv';
      link.click();
      URL.revokeObjectURL(url);
    },
    downloadPartDetail() {
      if (!this.partDetailRows.length) return;
      const wv = (val) => {
        const v = parseFloat(val) || 0;
        return this.weightUnit === 'kgs' ? Math.round(v * 1000) : v;
      };
      const headers = 'Date,Machine,Plan ID,Produced,Produced Wt (Kgs),Accepted,Rejected\n';
      const rows = this.partDetailRows.map((r) => [
        r.date,
        r.machinename,
        r.planid,
        r.produced,
        wv(r.producedweightinkgs),
        r.accepted,
        r.rejected,
      ].join(',')).join('\n');
      const csv = headers + rows;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = this.partDetailName.replace(/[^a-zA-Z0-9-_ ]/g, '');
      link.download = `${safeName}-production-detail.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    downloadTableData() {
      if (!this.groupedPlans.length) return;
      const headers = 'Part,Color,Quantity,Planned Tonnage,Actual Tonnage,Month,Machine\n';
      const rows = this.groupedPlans.map((r) => [
        `"${r.partname}"`,
        `"${r.color}"`,
        r.quantityDisplay,
        r.productionintonnage,
        r.actualDisplay || 0,
        this.formatMonth(r.monthyear),
        `"${r.machinename || '-'}"`,
      ].join(',')).join('\n');
      const csv = headers + rows;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `monthly-plan-${this.displayMonth.replace(' ', '-')}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    formatMonth(val) {
      if (!val) return '-';
      const s = String(val);
      let year;
      let month;
      if (s.includes('-')) {
        [year, month] = s.split('-');
      } else if (s.length >= 6) {
        year = s.substring(0, 4);
        month = s.substring(4, 6);
      } else {
        return s;
      }
      const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
      ];
      return `${months[parseInt(month, 10) - 1]} ${year}`;
    },
  },
};
</script>
